import React, { useEffect, useState } from 'react'
import { AppLayout } from '../../components/layout/AppLayout'
import { Button } from '../../components/ui/Button'
import { apiFetch, ApiError } from '../../lib/api-client'

interface Account {
  id: string
  account_no: string
  navn: string
  type: string
  pligtig_pengeydelse: boolean
  is_system: boolean
  saldo_oere: number
}

const ACCOUNT_TYPES = ['Indtægt', 'Udgift', 'Status', 'Suminterval'] as const

function kr(oere: number): string {
  return (oere / 100).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kr.'
}

const th: React.CSSProperties = { padding: '12px 20px', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'left', borderBottom: '1px solid #e8eaed' }
const td: React.CSSProperties = { padding: '12px 20px', fontSize: 13, color: '#111827', borderTop: '1px solid #f3f4f6' }
const input: React.CSSProperties = { width: '100%', height: 34, padding: '0 10px', borderRadius: 7, border: '1px solid #e8ecf4', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }

export function KontoplanPage() {
  const [accounts, setAccounts] = useState<Account[]>([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState({ account_no: '', navn: '', type: 'Indtægt', pligtig_pengeydelse: false })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reload = () => apiFetch<Account[]>('/api/v1/kontoplan').then(setAccounts).catch(() => {})

  useEffect(() => {
    apiFetch<Account[]>('/api/v1/kontoplan').then(setAccounts).catch(() => {}).finally(() => setLoading(false))
  }, [])

  const submit = async () => {
    if (!form.account_no.trim() || !form.navn.trim()) return
    setSaving(true)
    setError(null)
    try {
      await apiFetch('/api/v1/kontoplan', { method: 'POST', body: JSON.stringify(form) })
      setForm({ account_no: '', navn: '', type: 'Indtægt', pligtig_pengeydelse: false })
      setAdding(false)
      await reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Kunne ikke oprette konto.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <AppLayout pageTitle="Kontoplan">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>Kontoplan</div>
          <div style={{ fontSize: 13, color: '#64748b' }}>System- og brugerdefinerede konti med saldo fra posteringerne.</div>
        </div>
        <Button onClick={() => setAdding((v) => !v)}>{adding ? 'Annuller' : '+ Opret konto'}</Button>
      </div>

      {adding && (
        <div style={{ background: '#fff', border: '1px solid #e8ecf4', borderRadius: 10, padding: 16, marginBottom: 16, display: 'grid', gridTemplateColumns: '120px 1fr 160px auto', gap: 10, alignItems: 'center' }}>
          <input style={input} placeholder="Kontonr." value={form.account_no} onChange={(e) => setForm({ ...form, account_no: e.target.value })} />
          <input style={input} placeholder="Kontonavn" value={form.navn} onChange={(e) => setForm({ ...form, navn: e.target.value })} />
          <select style={{ ...input, padding: '0 8px' }} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
            {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <Button onClick={submit} isLoading={saving} disabled={!form.account_no.trim() || !form.navn.trim()}>Gem</Button>
          {error && <div style={{ gridColumn: '1 / -1', fontSize: 12, color: '#dc2626' }}>{error}</div>}
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={th}>Kontonr.</th>
              <th style={th}>Kontonavn</th>
              <th style={th}>Type</th>
              <th style={th}>Pligtig pengeydelse</th>
              <th style={{ ...th, textAlign: 'right' }}>Saldo</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td style={td} colSpan={5}>Henter…</td></tr>}
            {!loading && accounts.map((a) => (
              <tr key={a.id}>
                <td style={{ ...td, fontFamily: 'monospace', fontWeight: 600 }}>
                  {a.account_no}
                  <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, color: a.is_system ? '#94a3b8' : '#c2741b', fontFamily: 'inherit' }}>
                    {a.is_system ? 'System' : 'Brugerdefineret'}
                  </span>
                </td>
                <td style={{ ...td, fontWeight: 500 }}>{a.navn}</td>
                <td style={td}>{a.type}</td>
                <td style={td}>{a.pligtig_pengeydelse ? 'Ja' : 'Nej'}</td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: a.saldo_oere < 0 ? '#15803d' : '#111827' }}>{kr(a.saldo_oere)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppLayout>
  )
}
